import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { flushSync } from "react-dom";
import { Printer, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, PeriodInput, Loading, Empty, NativeSelect, StatPill } from "@/components/common";
import { NotaPages } from "@/components/NotaCard";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { currentPeriod, invNo, num, periodLabel, rupiah } from "@/lib/format";

const PER_PAGE = [4, 6, 8, 10, 12];
const replaced = (r) => r.print_count > 0 && (r.printed_revision ?? 0) < (r.revision || 0);

export default function PrintNota() {
  const { tenant } = useAuth();
  const [sp] = useSearchParams();
  const [period, setPeriod] = useState(sp.get("period") || currentPeriod());
  const [perPage, setPerPage] = useState(tenant?.notas_per_page || 8);
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState({});
  const [company, setCompany] = useState(tenant);
  const load = useCallback(async () => {
    try {
      const [inv, debt] = await Promise.all([
        api.get("/invoices", { params: { period, status: "approved" } }),
        api.get("/debts/previous", { params: { period } }).catch(() => ({ data: {} })),
      ]);
      const list = inv.data.map((r) => ({ ...r, prev_debt: debt.data[r.customer_id] || null }));
      setRows(list);
      setCompany(tenant);
      setPicked(Object.fromEntries(list.map((r) => [r.id, r.print_count === 0 || replaced(r)])));
    } catch (e) { toast.error(errMsg(e)); }
  }, [period, tenant]);
  useEffect(() => {
    load();
  }, [load]);
  const perPageOptions = [...new Set([...PER_PAGE, Number(perPage)])].sort((a, b) => a - b);
  const selected = (rows || []).filter((r) => picked[r.id]);
  const reprints = selected.filter((r) => r.print_count > 0);
  const allOn = rows?.length > 0 && selected.length === rows.length;
  const doPrint = async () => {
    try {
      const { data } = await api.post("/prints", { invoice_ids: selected.map((r) => r.id), notas_per_page: Number(perPage) });
      flushSync(() => setCompany(data.tenant));
      window.print();
      toast.success(data.print.reprint ? `Cetak ulang tercatat (${data.print.count} nota)` : `Nota siap dicetak (${data.print.count} nota)`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader sheet="SHEET 06" title="Cetak Bulk Nota" subtitle={`Hanya invoice yang sudah disahkan. Periode ${periodLabel(period)}.`}>
        <PeriodInput value={period} onChange={setPeriod} />
        <NativeSelect value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} data-testid="print-per-page-select">
          {perPageOptions.map((n) => <option key={n} value={n}>{n} nota / halaman A4</option>)}
        </NativeSelect>
        <Button onClick={doPrint} disabled={!selected.length} className="bg-emerald-700 hover:bg-emerald-800" data-testid="print-btn"><Printer className="h-4 w-4 mr-2" />Cetak {selected.length} Nota</Button>
      </PageHeader>
      {!rows ? <Loading /> : !rows.length ? <Empty>Belum ada invoice yang disahkan untuk periode ini.</Empty> : (
        <div className="grid xl:grid-cols-[380px_1fr] gap-6 fade-up">
          <div className="space-y-3 min-w-0">
            <div className="grid grid-cols-2 gap-3">
              <StatPill label="Nota Dipilih" value={num(selected.length)} tone="green" testId="print-stat-selected" />
              <StatPill label="Halaman A4" value={num(Math.ceil(selected.length / perPage))} testId="print-stat-pages" />
            </div>
            {reprints.length > 0 && (
              <div className="flex gap-2 text-xs bg-amber-50 border border-amber-300 text-amber-900 p-2 rounded-sm" data-testid="print-reprint-warning">
                <AlertTriangle className="h-4 w-4 shrink-0" />{reprints.length} nota sudah pernah dicetak. Pencetakan ulang akan tercatat di audit log.
              </div>
            )}
            <div className="xl-wrap max-h-[60vh]">
              <table className="xl">
                <thead><tr><th className="rownum"><Checkbox checked={allOn} onCheckedChange={(v) => setPicked(Object.fromEntries(rows.map((r) => [r.id, !!v])))} data-testid="print-select-all" /></th><th>No. Nota</th><th>Pelanggan</th><th className="text-right">Total</th><th>Cetak</th></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id}>
                      <td className="rownum"><Checkbox checked={!!picked[r.id]} onCheckedChange={(v) => setPicked((s) => ({ ...s, [r.id]: !!v }))} data-testid={`print-pick-${i}`} /></td>
                      <td className="font-mono text-xs">{invNo(r)}</td><td className="text-xs">{r.customer_name}</td>
                      <td className="num text-xs">{rupiah(r.total)}</td><td className="text-xs">{r.print_count}x{replaced(r) && <span className="ml-1 text-[10px] font-semibold px-1 py-0.5 rounded-sm bg-rose-100 text-rose-800 whitespace-nowrap" title="Nota lama sudah diganti versi baru" data-testid={`print-replaced-${i}`}>DIGANTI</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="min-w-0 bg-slate-200/70 border border-slate-300 rounded-sm p-4 overflow-auto max-h-[78vh]" data-testid="print-preview">
            {selected.length ? <div className="print-area"><NotaPages invoices={selected} tenant={company} perPage={perPage} /></div> : <p className="text-sm text-slate-500 text-center py-10">Pilih nota untuk melihat pratinjau A4.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
